import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import React, { MouseEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { EventDetailRow } from './EventDetailRow';
import {
  formatCategoriesWithBullet,
  formatEventDate,
  formatEventTime,
  getImageUrl,
  useLocale,
} from '@xolv-mybrightlink/common';
import { CmsEvent } from '../models/EventResult';

type EventItemProps = {
  event: CmsEvent;
  onBookmarkClicked?: (
    event: MouseEvent<SVGSVGElement>,
    isSaved: boolean,
  ) => void;
  onEventClicked?: () => void;
};

export const EventItem = React.forwardRef<HTMLDivElement, EventItemProps>(
  (props, ref) => {
    const locale = useLocale();
    return (
      <Box
        ref={ref}
        key={props.event.databaseId}
        sx={{
          display: 'flex',
          pt: 10,
          pb: 10,
          cursor: 'pointer',
        }}
        onClick={props.onEventClicked}
      >
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <img
            src={getImageUrl(
              props.event?.featuredImage?.node?.mediaDetails?.sizes,
            )}
            style={{
              width: 320,
              height: 240,
              borderRadius: 12,
              boxShadow:
                '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
              objectFit: 'cover',
            }}
            alt={props.event.title}
          />
          <Typography
            variant={'body2'}
            sx={{
              position: 'absolute',
              top: 0,
              background: '#626a7a',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px 0px 12px 0px',
            }}
          >
            {props.event.cost ?? <FormattedMessage id='resources.event.free' />}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            ml: 10,
            width: '100%',
          }}
        >
          <Box>
            <Typography variant={'h6'}>{props.event.title}</Typography>
            <EventDetailRow
              title={formatEventDate(
                props.event.startDate,
                props.event.endDate,
                locale,
              )}
              icon={
                <img
                  src='/public/assets/icons/ic_calendar.svg'
                  alt={'ic_calendar'}
                />
              }
            />
            <EventDetailRow
              title={formatEventTime(
                props.event.startDate,
                props.event.endDate,
              )}
              icon={
                <img src='/public/assets/icons/ic_clock.svg' alt={'ic_clock'} />
              }
            />
            {props.event.venue && (
              <EventDetailRow
                title={props.event.venue.title}
                icon={
                  <img
                    src='/public/assets/icons/ic_location.svg'
                    alt={'ic_location'}
                  />
                }
              />
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography>
              {formatCategoriesWithBullet(
                props.event?.eventsCategories?.nodes,
              ) || ''}
            </Typography>
            {props.event.isSaved ? (
              <BookmarkIcon
                sx={{
                  color: '#307fe2',
                  width: 40,
                  height: 40,
                }}
                onClick={(e) =>
                  props.onBookmarkClicked && props.onBookmarkClicked(e, false)
                }
              />
            ) : (
              <BookmarkBorderIcon
                sx={{
                  color: '#307fe2',
                  width: 40,
                  height: 40,
                }}
                onClick={(e) =>
                  props.onBookmarkClicked && props.onBookmarkClicked(e, true)
                }
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);
EventItem.displayName = 'EventItem';

export default EventItem;
